import React from "react";
import { Box, styled, Typography } from "@mui/material";
import { useIsMobile } from "../../../../../hooks/responsive";
import { useOdysseeTheme } from "../../../../../context/odysseeTheme/OdysseeThemeProvider";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

const CardContainer = styled(Box)(() => ({
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 20,
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    position: "relative",
    minHeight: 250,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
    },
}));

const IntervenantSection = styled(Box)(() => ({
    width: 200,
    minWidth: 200,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
}));

const IntervenantImageContainer = styled(Box)(() => ({
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#1F17237D",
    "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "top center",
    },
}));

const IntervenantName = styled(Box)(() => ({
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "15px",
    paddingLeft: "20px",
    paddingTop: "50px",
    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
}));

const ContentSection = styled(Box)(() => ({
    flex: 1,
    padding: "20px",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: 20,
}));

const DateBoxContainer = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: 12,
    overflow: "hidden",
    minWidth: 120,
    height: "fit-content",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
}));

const DateBoxTop = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "#FDFBF7",
    padding: "10px 20px",
    width: "100%",
}));

const TimeBoxBottom = styled(Box)(() => ({
    background: "#C7C4DE",
    padding: "8px 16px",
    width: "100%",
    textAlign: "center",
    textTransform: "capitalize",
    border: "none"
}));

const InfoSection = styled(Box)(() => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
}));

const ActionButton = styled(Box)(() => ({
    background: "rgba(255, 255, 255, 0.15)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: 30,
    padding: "12px 32px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    marginTop: 24,
    width: "fit-content",
    alignSelf: "flex-start",
    "&:hover": {
        background: "rgba(255, 255, 255, 0.25)",
        borderColor: "rgba(255, 255, 255, 0.5)",
    },
}));

const FutureLiveCard = ({ webinaire, onPlayClick }) => {
    const isMobile = useIsMobile();
    const firstIntervenant = webinaire?.intervenants?.[0];

    const dateInfo = React.useMemo(() => {
        if (!webinaire?.startAt) return {};
        const date = moment(webinaire.startAt * 1000);
        return {
            dayName: date.format("dddd"),
            day: date.format("D"),
            month: date.format("MMM YYYY"),
        };
    }, [webinaire?.startAt]);

    const timeRange = React.useMemo(() => {
        if (!webinaire?.startAt || !webinaire?.endAt) return "";
        const start = moment(webinaire.startAt * 1000).format("HH[h]mm");
        const end = moment(webinaire.endAt * 1000).format("HH[h]mm");
        return { start, end };
    }, [webinaire?.startAt, webinaire?.endAt]);

    const handleClick = () => {
        if (onPlayClick) {
            onPlayClick();
            return;
        }
        if (webinaire?.link) {
            window.open(webinaire.link, "_blank");
        }
    };

    const [titleColor, contentColor] = useOdysseeTheme("card", [
        "header.title",
        "header.content",
    ]);

    const isOnline = webinaire?.status === "online";

    if (isMobile) {
        return (
            <Box
                sx={{
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "15px",
                    overflow: "hidden",
                    cursor: "pointer",
                    position: "relative",
                }}
                onClick={isOnline ? handleClick : undefined}
            >
                {/* Mobile */}
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    {firstIntervenant?.image && (
                        <Box
                            sx={{
                                width: "70%",
                                minHeight: 180,
                                position: "relative",
                                borderBottomRightRadius: 10,
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={firstIntervenant.image}
                                alt={`${firstIntervenant.prenom} ${firstIntervenant.nom}`}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "top center",
                                }}
                            />
                            <IntervenantName>
                                <Typography
                                    color={titleColor || "#ffffff"}
                                    fontFamily="Branding"
                                    fontWeight={600}
                                    fontSize={14}
                                >
                                    {firstIntervenant.prenom} {firstIntervenant.nom}
                                </Typography>
                            </IntervenantName>
                        </Box>
                    )}
                    <Box sx={{ width: "50%", padding: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <DateBoxContainer sx={{ minWidth: 110 }}>
                            <TimeBoxBottom sx={{ padding: "10px 14px" }}>
                                <Typography color="#2D2A4A" fontFamily="Branding" fontWeight={600} fontSize={12}>
                                    {dateInfo.dayName}
                                </Typography>
                            </TimeBoxBottom>
                            <DateBoxTop sx={{ padding: "14px 18px" }}>
                                <Typography color="#2D2A4A" fontFamily="Yeseva One" fontSize={36} lineHeight={1.1}>
                                    {dateInfo.day}
                                </Typography>
                                <Typography color="#7B6B8D" fontFamily="Branding" fontSize={12} textTransform="capitalize">
                                    {dateInfo.month}
                                </Typography>
                            </DateBoxTop>
                            <TimeBoxBottom sx={{ padding: "10px 14px" }}>
                                <Typography color="#2D2A4A" fontFamily="Branding" fontWeight={600} fontSize={12}>
                                    De {timeRange.start} à {timeRange.end}
                                </Typography>
                            </TimeBoxBottom>
                        </DateBoxContainer>
                    </Box>
                    {isOnline && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 10,
                                left: 10,
                                backgroundColor: "#FF4626",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                zIndex: 10,
                            }}
                        >
                            <Typography
                                color="#ffffff"
                                fontFamily="Branding"
                                fontWeight={700}
                                fontSize={10}
                                textTransform="uppercase"
                            >
                                En direct
                            </Typography>
                        </Box>
                    )}
                </Box>
                {/* Mobile */}
                <Box sx={{ padding: "16px 20px" }}>
                    <Typography
                        color={titleColor || "#ffffff"}
                        fontFamily="Branding Bold"
                        fontSize={20}
                        lineHeight={1.2}
                        mb={1}
                    >
                        {webinaire?.title}
                    </Typography>
                    {webinaire?.description && (
                        <Typography
                            color={contentColor || "rgba(255,255,255,0.7)"}
                            fontFamily="Branding"
                            fontWeight={500}
                            fontSize={13}
                            dangerouslySetInnerHTML={{ __html: webinaire.description }}
                            sx={{
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        />
                    )}
                    {isOnline && (
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 2 }}>
                            <ActionButton sx={{ m: 0 }}>
                                <Typography
                                    color={titleColor || "#ffffff"}
                                    fontFamily="Branding"
                                    fontWeight={600}
                                    fontSize={14}
                                >
                                    Accéder au direct
                                </Typography>
                            </ActionButton>
                        </Box>
                    )}
                </Box>
            </Box >
        );
    }


    return (
        <CardContainer onClick={isOnline ? handleClick : undefined} sx={{ cursor: "pointer" }}>
            {firstIntervenant?.image && (
                <IntervenantSection>
                    <IntervenantImageContainer>
                        <img
                            src={firstIntervenant.image}
                            alt={`${firstIntervenant.prenom} ${firstIntervenant.nom}`}
                        />
                    </IntervenantImageContainer>
                    <IntervenantName>
                        <Typography
                            color={titleColor || "#ffffff"}
                            fontFamily="Branding"
                            fontWeight={600}
                            fontSize={18}
                        >
                            {firstIntervenant.prenom} {firstIntervenant.nom}
                        </Typography>
                    </IntervenantName>
                </IntervenantSection>
            )}

            {
                isOnline && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 15,
                            left: 15,
                            backgroundColor: "#FF4626",
                            padding: "5px 12px",
                            borderRadius: "20px",
                            zIndex: 10,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                    >
                        <Typography
                            color="#ffffff"
                            fontFamily="Branding"
                            fontWeight={700}
                            fontSize={12}
                            textTransform="uppercase"
                            sx={{ letterSpacing: "0.5px" }}
                        >
                            En direct
                        </Typography>
                    </Box>
                )
            }

            <ContentSection>
                <DateBoxContainer>
                    <TimeBoxBottom sx={{ padding: "10px 14px" }}>
                        <Typography color="#2D2A4A" fontFamily="Branding" fontWeight={600} fontSize={15}>
                            {dateInfo.dayName}
                        </Typography>
                    </TimeBoxBottom>
                    <DateBoxTop>
                        <Typography
                            color="#2D2A4A"
                            fontFamily="Yeseva One"
                            fontSize={42}
                            lineHeight={1.1}
                        >
                            {dateInfo.day}
                        </Typography>
                        <Typography
                            color="#7B6B8D"
                            fontFamily="Branding"
                            fontWeight={500}
                            fontSize={13}
                            textTransform="capitalize"
                        >
                            {dateInfo.month}
                        </Typography>
                    </DateBoxTop>
                    <TimeBoxBottom>
                        <Typography
                            color="#2D2A4A"
                            fontFamily="Branding"
                            fontWeight={600}
                            fontSize={13}
                        >
                            De {timeRange.start} à {timeRange.end}
                        </Typography>
                    </TimeBoxBottom>
                </DateBoxContainer>


                <InfoSection>
                    <Typography
                        color={titleColor || "#ffffff"}
                        fontFamily="Branding Bold"
                        fontSize={24}
                        lineHeight={1.2}
                        mb={2}
                    >
                        {webinaire?.title}
                    </Typography>
                    {webinaire?.description && (
                        <Typography
                            color={contentColor || "rgba(255,255,255,0.7)"}
                            fontFamily="Branding"
                            fontWeight={500}
                            fontSize={13}
                            dangerouslySetInnerHTML={{ __html: webinaire.description }}
                            sx={{
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                            }}
                        />
                    )}
                    {isOnline && (
                        <ActionButton>
                            <Typography
                                color={titleColor || "#ffffff"}
                                fontFamily="Branding"
                                fontWeight={600}
                                fontSize={15}
                            >
                                Accéder au direct
                            </Typography>
                        </ActionButton>
                    )}
                </InfoSection>
            </ContentSection>
        </CardContainer >
    );
};

export default FutureLiveCard;
